/**
 * WorkflowStarter — Dynamic order form for initiating workflows.
 * Parses template variables and renders appropriate input fields.
 * Handles validation, file uploads, and execution submission.
 */
import React, { useState, useCallback } from 'react';
import { apiCall } from '../../utils/apiUtils';

const API_BASE = '/api/external/v1/workflows';

/**
 * Renders a single variable input based on its type.
 */
const VariableInput = ({ variable, value, onChange, error }) => {
    const baseInputStyle = {
        width: '100%',
        background: '#0F172A',
        border: `1px solid ${error ? '#EF4444' : 'rgba(255,255,255,0.1)'}`,
        borderRadius: '8px',
        padding: '10px 14px',
        color: '#F1F5F9',
        fontSize: '14px',
        outline: 'none',
        boxSizing: 'border-box',
        transition: 'border-color 0.2s',
    };

    switch (variable.type) {
        case 'string':
            return (
                <input
                    type="text"
                    value={value || ''}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={variable.description || `Enter ${variable.name}`}
                    style={baseInputStyle}
                    onFocus={(e) => { e.target.style.borderColor = '#6366F1'; }}
                    onBlur={(e) => { e.target.style.borderColor = error ? '#EF4444' : 'rgba(255,255,255,0.1)'; }}
                />
            );

        case 'bool':
            return (
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                    <div
                        onClick={() => onChange(!value)}
                        style={{
                            width: '44px',
                            height: '24px',
                            borderRadius: '12px',
                            background: value ? '#6366F1' : '#334155',
                            position: 'relative',
                            cursor: 'pointer',
                            transition: 'background 0.2s ease',
                        }}
                    >
                        <div style={{
                            width: '20px',
                            height: '20px',
                            borderRadius: '50%',
                            background: 'white',
                            position: 'absolute',
                            top: '2px',
                            left: value ? '22px' : '2px',
                            transition: 'left 0.2s ease',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                        }} />
                    </div>
                    <span style={{ color: '#CBD5E1', fontSize: '13px' }}>
                        {value ? 'Yes' : 'No'}
                    </span>
                </label>
            );

        case 'file':
            return (
                <div>
                    <label style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        border: `1px dashed ${error ? '#EF4444' : 'rgba(255,255,255,0.15)'}`,
                        background: 'rgba(255,255,255,0.02)',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                    }}>
                        <svg width="20" height="20" fill="none" stroke="#94A3B8" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        <span style={{ color: '#94A3B8', fontSize: '13px' }}>
                            {value?.name || 'Choose a file...'}
                        </span>
                        <input
                            type="file"
                            style={{ display: 'none' }}
                            onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) onChange(file);
                            }}
                        />
                    </label>
                    {value?.name && (
                        <div style={{ marginTop: '6px', color: '#64748B', fontSize: '11px' }}>
                            {value.name} ({(value.size / 1024).toFixed(1)} KB)
                        </div>
                    )}
                </div>
            );

        default:
            return (
                <input
                    type="text"
                    value={value || ''}
                    onChange={(e) => onChange(e.target.value)}
                    style={baseInputStyle}
                />
            );
    }
};

const WorkflowStarter = ({ template, onClose, onStarted }) => {
    const [formValues, setFormValues] = useState({});
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);

    const variables = template?.variables || [];

    const handleChange = useCallback((varName, value) => {
        setFormValues((prev) => ({ ...prev, [varName]: value }));
        // Clear error on change
        setErrors((prev) => {
            const next = { ...prev };
            delete next[varName];
            return next;
        });
    }, []);

    const validate = () => {
        const newErrors = {};
        for (const v of variables) {
            if (v.required) {
                const val = formValues[v.name];
                if (val === undefined || val === null || val === '') {
                    newErrors[v.name] = `${v.name} is required`;
                }
            }
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;

        setSubmitting(true);
        setSubmitError(null);

        try {
            // Process file variables: upload first, then use document_id
            const processedVars = {};
            for (const v of variables) {
                const val = formValues[v.name];
                if (val === undefined || val === null) continue;

                if (v.type === 'file' && val instanceof File) {
                    // Upload file to get a document reference
                    const formData = new FormData();
                    formData.append('file', val);
                    const baseURL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
                    const uploadRes = await fetch(`${baseURL}/api/orders/upload-temp`, {
                        method: 'POST',
                        credentials: 'include',
                        body: formData,
                    });
                    if (!uploadRes.ok) {
                        throw new Error(`File upload failed: ${uploadRes.statusText}`);
                    }
                    const uploadData = await uploadRes.json();
                    processedVars[v.name] = uploadData.document_id || uploadData.file_id || val.name;
                } else {
                    processedVars[v.name] = val;
                }
            }

            // Start execution
            const data = await apiCall(`${API_BASE}/executions`, {
                method: 'POST',
                data: {
                    template_id: template.id,
                    variables: processedVars,
                },
            });

            if (onStarted) onStarted(data);
            onClose();
        } catch (err) {
            setSubmitError(err.message || 'Failed to start workflow');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
            <div style={{
                width: '520px', maxHeight: '80vh', background: '#1E293B',
                borderRadius: '16px', border: '1px solid rgba(255,255,255,0.1)',
                boxShadow: '0 24px 48px rgba(0,0,0,0.4)',
                display: 'flex', flexDirection: 'column', overflow: 'hidden',
            }}>
                {/* Header */}
                <div style={{
                    padding: '20px 24px 16px',
                    borderBottom: '1px solid rgba(255,255,255,0.06)',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                }}>
                    <div>
                        <h2 style={{ color: '#F1F5F9', fontSize: '18px', fontWeight: 700, margin: 0 }}>
                            Start Workflow
                        </h2>
                        <p style={{ color: '#94A3B8', fontSize: '13px', marginTop: '4px' }}>
                            {template.name}
                        </p>
                        {template.description && (
                            <p style={{ color: '#64748B', fontSize: '12px', marginTop: '2px' }}>
                                {template.description}
                            </p>
                        )}
                    </div>
                    <button
                        onClick={onClose}
                        style={{ background: 'none', border: 'none', color: '#94A3B8', cursor: 'pointer', fontSize: '20px', padding: '4px' }}
                    >
                        ✕
                    </button>
                </div>

                {/* Form */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
                    {variables.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '24px', color: '#64748B' }}>
                            <p style={{ fontSize: '14px' }}>This workflow has no required inputs.</p>
                            <p style={{ fontSize: '12px', marginTop: '8px' }}>Click "Start" below to run it immediately.</p>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
                            {variables.map((v) => (
                                <div key={v.name}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                                        <label style={{
                                            color: '#E2E8F0', fontSize: '13px', fontWeight: 600,
                                        }}>
                                            {v.name}
                                        </label>
                                        {v.required && (
                                            <span style={{ color: '#EF4444', fontSize: '12px' }}>*</span>
                                        )}
                                        <span style={{
                                            marginLeft: 'auto',
                                            color: '#475569', fontSize: '10px', textTransform: 'uppercase',
                                            padding: '2px 6px', borderRadius: '4px',
                                            background: 'rgba(255,255,255,0.05)',
                                        }}>
                                            {v.type}
                                        </span>
                                    </div>
                                    {v.description && (
                                        <p style={{ color: '#64748B', fontSize: '11px', marginBottom: '6px' }}>
                                            {v.description}
                                        </p>
                                    )}
                                    <VariableInput
                                        variable={v}
                                        value={formValues[v.name]}
                                        onChange={(val) => handleChange(v.name, val)}
                                        error={errors[v.name]}
                                    />
                                    {errors[v.name] && (
                                        <p style={{ color: '#EF4444', fontSize: '11px', marginTop: '4px' }}>
                                            {errors[v.name]}
                                        </p>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Error */}
                    {submitError && (
                        <div style={{
                            marginTop: '16px', padding: '10px 14px', borderRadius: '8px',
                            background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)',
                            color: '#EF4444', fontSize: '13px',
                        }}>
                            {submitError}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div style={{
                    padding: '16px 24px',
                    borderTop: '1px solid rgba(255,255,255,0.06)',
                    display: 'flex', justifyContent: 'flex-end', gap: '10px',
                }}>
                    <button
                        onClick={onClose}
                        disabled={submitting}
                        style={{
                            padding: '10px 20px', borderRadius: '8px', fontSize: '13px', fontWeight: 600,
                            background: 'transparent', border: '1px solid rgba(255,255,255,0.15)',
                            color: '#94A3B8', cursor: 'pointer',
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={submitting}
                        style={{
                            padding: '10px 24px', borderRadius: '8px', fontSize: '13px', fontWeight: 600,
                            background: submitting ? '#4338CA' : '#6366F1', border: 'none',
                            color: 'white', cursor: submitting ? 'not-allowed' : 'pointer',
                            opacity: submitting ? 0.7 : 1,
                            transition: 'all 0.2s ease',
                        }}
                    >
                        {submitting ? 'Starting...' : 'Start Workflow'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default WorkflowStarter;
